import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      // Demo режимд бүх эмэйлийг зөвшөөрнө
      const demoUser = {
        uid: `demo-${Date.now()}`,
        email: email,
        displayName: email.split('@')[0]
      } as User;
      setCurrentUser(demoUser);
      
      // Админ эрхийг шалгах
      const adminEmails = ['admin@burger.mn', 'manager@burger.mn', 'admin@gmail.com'];
      if (adminEmails.includes(email) || email.includes('admin')) {
        setIsAdmin(true);
      }
      
      console.log(`Demo режимд нэвтэрлээ: ${email}`);
      return;
    } catch (error: any) {
      console.error('Нэвтрэхэд алдаа:', error);
      throw new Error('Нэвтрэхэд алдаа гарлаа');
    }
  };

  const register = async (email: string, password: string): Promise<void> => {
    try {
      // Demo режимд бүх эмэйлийг зөвшөөрнө
      const demoUser = {
        uid: `demo-${Date.now()}`,
        email: email,
        displayName: email.split('@')[0]
      } as User;
      setCurrentUser(demoUser);
      
      console.log(`Demo режимд бүртгүүллээ: ${email}`);
      return;
    } catch (error: any) {
      console.error('Бүртгүүлэхэд алдаа:', error);
      throw new Error('Бүртгүүлэхэд алдаа гарлаа');
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      setIsAdmin(false);
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    // Админ эмэйлүүдийн жагсаалт (энэ нь Firebase Firestore-д хадгалагдах ёстой)
    const adminEmails = ['admin@burger.mn', 'manager@burger.mn'];
    
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user && adminEmails.includes(user.email || '')) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    login,
    register,
    logout,
    loading,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
